import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { format, parseISO, formatDistanceToNow, isSameDay } from "date-fns";
import {
  Loader2,
  ArrowLeft,
  Search,
  GraduationCap,
  Calendar as CalIcon,
  Phone,
  Mail,
  Building2,
  MapPin,
  X,
  RefreshCw,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { supabase } from "@/integrations/supabase/client";
import { hasAdminAccess } from "@/lib/admin-access";
import { waDigits as waDigitsOf } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { listClientIntakes, updateClientIntakeStatus, type ClientIntakeRow } from "@/lib/client-intakes.functions";
import { syncPaymentsFromSheet } from "@/lib/payments-sync.functions";

const LAST_SYNC_KEY = "welivdit:intake:last-sync";
const LAST_PAYMENTS_SYNC_KEY = "welivdit:payments:last-sync";

export const Route = createFileRoute("/admin/intake")({
  head: () => ({
    meta: [{ title: "Paid Client Intake - Admin" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: ClientIntakePage,
});

const STATUS_OPTIONS = [
  {
    value: "pending_review",
    label: "Pending Review",
    color: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-900",
  },
  {
    value: "ds160_checked",
    label: "DS-160 Checked",
    color: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-900",
  },
  {
    value: "mocks_scheduled",
    label: "Mocks Scheduled",
    color:
      "bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-950 dark:text-violet-200 dark:border-violet-900",
  },
  {
    value: "completed",
    label: "Completed",
    color:
      "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-200 dark:border-emerald-900",
  },
  {
    value: "visa_approved",
    label: "Visa Approved ✓",
    color: "bg-green-200 text-green-900 border-green-300 dark:bg-green-900 dark:text-green-100 dark:border-green-800",
  },
  {
    value: "rejected",
    label: "Refused",
    color: "bg-red-50 text-red-600 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900",
  },
] as const;

function statusMeta(s: string) {
  return STATUS_OPTIONS.find((o) => o.value === s) ?? STATUS_OPTIONS[0];
}

function fmtDate(s: string | null) {
  if (!s) return "-";
  try {
    return format(parseISO(s), "d MMM yyyy");
  } catch {
    return s;
  }
}

function ClientIntakePage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        navigate({ to: "/auth", search: { redirect: "/admin/intake" } });
        return;
      }
      setAuthorized(await hasAdminAccess(sess.session));
      setAuthChecked(true);
    })();
  }, [navigate]);

  const fetchList = useServerFn(listClientIntakes);
  const updateStatus = useServerFn(updateClientIntakeStatus);
  const syncSheet = useServerFn(syncPaymentsFromSheet);

  const [rows, setRows] = useState<ClientIntakeRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [, forceTick] = useState(0);

  useEffect(() => {
    try {
      const v = localStorage.getItem(LAST_SYNC_KEY);
      if (v) setLastSyncedAt(v);
    } catch {
      /* ignore */
    }
  }, []);

  // Re-render the "x minutes ago" label every 30s
  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    // Retry once on transient network / cold-start failures - the sync
    // occasionally causes a brief worker restart which surfaces as
    // "Failed to fetch" on the very next call.
    const attempt = async () => await fetchList();
    try {
      let res;
      try {
        res = await attempt();
      } catch {
        await new Promise((r) => setTimeout(r, 1500));
        res = await attempt();
      }
      setRows(res.rows);
    } catch (e) {
      toast.error("Failed to load intakes - please refresh in a moment");
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [fetchList]);

  useEffect(() => {
    if (authorized) void refetch();
  }, [authorized, refetch]);

  const handleSync = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await syncSheet();
      const ts = res.syncedAt ?? new Date().toISOString();
      setLastSyncedAt(ts);
      try {
        localStorage.setItem(LAST_SYNC_KEY, ts);
        localStorage.setItem(LAST_PAYMENTS_SYNC_KEY, ts);
      } catch {
        /* ignore */
      }
      await refetch();
      toast.success(
        `Synced ${res.totalIntakes} intake${res.totalIntakes === 1 ? "" : "s"} · ${res.upserted} paid client${res.upserted === 1 ? "" : "s"} updated`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Sync failed");
      console.error(e);
    } finally {
      setSyncing(false);
    }
  }, [syncing, syncSheet, refetch]);

  const applyUpdate = useCallback(
    async (id: string, admin_status: ClientIntakeRow["admin_status"], admin_notes?: string) => {
      const prev = rows;
      setRows((rs) =>
        rs.map((r) =>
          r.id === id ? { ...r, admin_status, ...(admin_notes !== undefined ? { admin_notes } : {}) } : r,
        ),
      );
      setPending(true);
      try {
        await updateStatus({ data: { id, admin_status, admin_notes } });
        toast.success("Updated");
      } catch (e) {
        setRows(prev);
        toast.error("Failed to update");
        console.error(e);
      } finally {
        setPending(false);
      }
    },
    [rows, updateStatus],
  );

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<string>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [visaDateFilter, setVisaDateFilter] = useState<Date | undefined>(undefined);
  const [visaDateMode, setVisaDateMode] = useState<"any" | "exact" | "upcoming" | "past" | "unscheduled">("any");

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: rows.length };
    for (const o of STATUS_OPTIONS) c[o.value] = 0;
    for (const r of rows) c[r.admin_status] = (c[r.admin_status] ?? 0) + 1;
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return rows.filter((r) => {
      if (filter !== "all" && r.admin_status !== filter) return false;

      // Visa date filter
      if (visaDateMode !== "any") {
        const vd = r.visa_date ? parseISO(r.visa_date) : null;
        if (visaDateMode === "unscheduled") {
          if (vd) return false;
        } else if (!vd) {
          return false;
        } else if (visaDateMode === "exact" && visaDateFilter) {
          if (!isSameDay(vd, visaDateFilter)) return false;
        } else if (visaDateMode === "upcoming") {
          if (vd < today) return false;
        } else if (visaDateMode === "past") {
          if (vd >= today) return false;
        }
      }

      if (!q) return true;
      return (
        r.full_name.toLowerCase().includes(q) ||
        (r.university ?? "").toLowerCase().includes(q) ||
        (r.email ?? "").toLowerCase().includes(q) ||
        (r.plan ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, filter, visaDateMode, visaDateFilter]);

  const selected = rows.find((r) => r.id === selectedId) ?? null;

  if (!authChecked)
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  if (!authorized)
    return (
      <div className="min-h-[60vh] grid place-items-center px-5">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl">Not authorized</h1>
          <p className="mt-2 text-muted-foreground">Admins only.</p>
        </div>
      </div>
    );

  return (
    <section className="mx-auto max-w-7xl px-5 py-10">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to admin
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-primary uppercase tracking-wider">Admin</p>
          <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold">Paid Client Intake</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {rows.length} client{rows.length === 1 ? "" : "s"} synced from the intake form
          </p>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-2 w-full md:w-auto">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] text-muted-foreground"
              aria-live="polite"
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  syncing ? "bg-amber-500 animate-pulse" : lastSyncedAt ? "bg-emerald-500" : "bg-muted-foreground/50"
                }`}
                aria-hidden="true"
              />
              {syncing
                ? "Syncing…"
                : lastSyncedAt
                  ? `Last synced: ${format(parseISO(lastSyncedAt), "MMM d, h:mm a")} (${formatDistanceToNow(parseISO(lastSyncedAt), { addSuffix: true })})`
                  : "Never synced"}
            </span>
            <Button type="button" size="sm" onClick={() => void handleSync()} disabled={syncing} className="gap-1.5">
              {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              {syncing ? "Syncing" : "Sync Latest"}
            </Button>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search name, university, plan…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="mt-6 flex flex-wrap gap-2">
        <FilterChip label="All" count={counts.all} active={filter === "all"} onClick={() => setFilter("all")} />
        {STATUS_OPTIONS.map((o) => (
          <FilterChip
            key={o.value}
            label={o.label}
            count={counts[o.value] ?? 0}
            active={filter === o.value}
            onClick={() => setFilter(o.value)}
            color={o.color}
          />
        ))}
      </div>

      {/* Visa date filter */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mr-1">Visa date:</span>
        <FilterChip
          label="Any"
          count={rows.length}
          active={visaDateMode === "any"}
          onClick={() => {
            setVisaDateMode("any");
            setVisaDateFilter(undefined);
          }}
        />
        <FilterChip
          label="Upcoming"
          count={
            rows.filter((r) => {
              if (!r.visa_date) return false;
              const d = parseISO(r.visa_date);
              const t = new Date();
              t.setHours(0, 0, 0, 0);
              return d >= t;
            }).length
          }
          active={visaDateMode === "upcoming"}
          onClick={() => {
            setVisaDateMode("upcoming");
            setVisaDateFilter(undefined);
          }}
        />
        <FilterChip
          label="Past"
          count={
            rows.filter((r) => {
              if (!r.visa_date) return false;
              const d = parseISO(r.visa_date);
              const t = new Date();
              t.setHours(0, 0, 0, 0);
              return d < t;
            }).length
          }
          active={visaDateMode === "past"}
          onClick={() => {
            setVisaDateMode("past");
            setVisaDateFilter(undefined);
          }}
        />
        <FilterChip
          label="Unscheduled"
          count={rows.filter((r) => !r.visa_date).length}
          active={visaDateMode === "unscheduled"}
          onClick={() => {
            setVisaDateMode("unscheduled");
            setVisaDateFilter(undefined);
          }}
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "h-auto gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium",
                visaDateMode === "exact" &&
                  visaDateFilter &&
                  "border-primary bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
              )}
            >
              <CalIcon className="h-3.5 w-3.5" />
              {visaDateMode === "exact" && visaDateFilter ? format(visaDateFilter, "d MMM yyyy") : "Pick a date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={visaDateFilter}
              onSelect={(d) => {
                if (d) {
                  setVisaDateFilter(d);
                  setVisaDateMode("exact");
                }
              }}
              initialFocus
              className={cn("p-3 pointer-events-auto")}
            />
          </PopoverContent>
        </Popover>
        {visaDateMode !== "any" && (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto rounded-full px-2 py-1.5 text-xs text-muted-foreground"
            onClick={() => {
              setVisaDateMode("any");
              setVisaDateFilter(undefined);
            }}
          >
            <X className="h-3.5 w-3.5 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Card grid */}
      <div className="mt-6">
        {isLoading ? (
          <div className="grid place-items-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border py-16 text-center text-sm text-muted-foreground">
            No clients match your filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((r) => {
              const sm = statusMeta(r.admin_status);
              return (
                <button
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  className="group text-left rounded-2xl border border-border bg-card p-4 hover:border-primary hover:shadow-md transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-display text-base font-semibold truncate group-hover:text-primary transition-colors">
                        {r.full_name}
                      </h3>
                      <p className="mt-1 text-xs text-muted-foreground line-clamp-2 min-h-[2rem]">
                        <Building2 className="inline h-3 w-3 mr-1 -mt-0.5" />
                        {r.university || "-"}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${sm.color}`}
                    >
                      {sm.label}
                    </span>
                    {r.plan && (
                      <span className="inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 text-[10px] text-muted-foreground">
                        {r.plan}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t border-border/60 text-[11px] text-muted-foreground space-y-1">
                    {r.visa_date && (
                      <p className="inline-flex items-center gap-1">
                        <CalIcon className="h-3 w-3" /> Visa {fmtDate(r.visa_date)} · {r.visa_city || "-"}
                      </p>
                    )}
                    <p className="truncate">Submitted {fmtDate(r.submitted_at)}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Slide-out details */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelectedId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          {selected && (
            <DetailPanel
              row={selected}
              onStatusChange={(s) => void applyUpdate(selected.id, s)}
              onNotesBlur={(notes) => {
                if (notes !== (selected.admin_notes ?? "")) void applyUpdate(selected.id, selected.admin_status, notes);
              }}
              pending={pending}
            />
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
  color,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  color?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : color
            ? `${color} hover:opacity-80`
            : "border-border bg-card text-foreground hover:border-primary/40"
      }`}
    >
      {label}
      <span
        className={`rounded-full px-1.5 py-0.5 text-[10px] ${active ? "bg-primary-foreground/20" : "bg-background/60"}`}
      >
        {count}
      </span>
    </button>
  );
}

function DetailPanel({
  row,
  onStatusChange,
  onNotesBlur,
  pending,
}: {
  row: ClientIntakeRow;
  onStatusChange: (s: ClientIntakeRow["admin_status"]) => void;
  onNotesBlur: (notes: string) => void;
  pending: boolean;
}) {
  const [notes, setNotes] = useState(row.admin_notes ?? "");
  useEffect(() => setNotes(row.admin_notes ?? ""), [row.id, row.admin_notes]);
  const sm = statusMeta(row.admin_status);
  const waPhone = waDigitsOf(row.whatsapp);

  // Mock/session entries from raw_data
  const mockEntries = Object.entries(row.raw_data ?? {}).filter(
    ([k, v]) => v != null && String(v).trim() !== "" && /mock|session/i.test(k),
  );

  return (
    <>
      <SheetHeader>
        <SheetTitle className="font-display text-2xl">{row.full_name}</SheetTitle>
        <SheetDescription className="flex items-center gap-2 flex-wrap">
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${sm.color}`}
          >
            {sm.label}
          </span>
          {row.plan && <Badge variant="outline">{row.plan}</Badge>}
        </SheetDescription>
      </SheetHeader>

      <div className="mt-6 space-y-5">
        {/* Status updater */}
        <div className="rounded-xl border border-border bg-card p-4">
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Operational status
          </label>
          <Select
            value={row.admin_status}
            onValueChange={(v) => onStatusChange(v as ClientIntakeRow["admin_status"])}
            disabled={pending}
          >
            <SelectTrigger className="mt-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Contact */}
        <DetailSection title="Contact">
          <DetailRow icon={Mail} label="Email" value={row.email} href={`mailto:${row.email}`} />
          <DetailRow
            icon={Phone}
            label="WhatsApp"
            value={row.whatsapp || "-"}
            href={waPhone ? `https://wa.me/${waPhone}` : undefined}
          />
        </DetailSection>

        {/* Academic */}
        <DetailSection title="Academic">
          <DetailRow icon={GraduationCap} label="University" value={row.university || "-"} />
          <DetailRow icon={Building2} label="Program" value={row.program || "-"} />
        </DetailSection>

        {/* Visa */}
        <DetailSection title="Visa appointment">
          <DetailRow icon={CalIcon} label="Booked?" value={row.has_booked_interview || "-"} />
          <DetailRow
            icon={MapPin}
            label="Visa interview"
            value={`${fmtDate(row.visa_date)} · ${row.visa_city || "-"}`}
          />
          <DetailRow
            icon={MapPin}
            label="Biometrics (OFC)"
            value={`${fmtDate(row.biometrics_date)} · ${row.biometrics_city || "-"}`}
          />
        </DetailSection>

        {/* Mocks / sessions */}
        {mockEntries.length > 0 && (
          <DetailSection title="Mock & session bookings">
            <div className="space-y-1.5">
              {mockEntries.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 text-xs">
                  <span className="text-muted-foreground">{k.replace(/\s*\d+$/, "")}</span>
                  <span className="text-right font-medium">{String(v)}</span>
                </div>
              ))}
            </div>
          </DetailSection>
        )}

        {/* Meta */}
        <DetailSection title="Meta">
          <DetailRow icon={CalIcon} label="Form submitted" value={fmtDate(row.submitted_at)} />
        </DetailSection>

        {/* Admin notes */}
        <div className="rounded-xl border border-border bg-card p-4">
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Admin notes</label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => onNotesBlur(notes)}
            placeholder="Private notes about this client…"
            className="mt-2 min-h-[100px]"
          />
          <p className="mt-1 text-[10px] text-muted-foreground">Saves on blur</p>
        </div>
      </div>
    </>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{title}</p>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: typeof X;
  label: string;
  value: string;
  href?: string;
}) {
  const content = <span className={href ? "text-primary hover:underline break-all" : "break-words"}>{value}</span>;
  return (
    <div className="flex items-start gap-2.5 text-sm">
      <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] text-muted-foreground">{label}</p>
        {href ? (
          <a href={href} target="_blank" rel="noreferrer">
            {content}
          </a>
        ) : (
          content
        )}
      </div>
    </div>
  );
}
